import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query'
import { normaliseDecimal } from '@/features/finance/lines'
import type {
  DebitNote,
  DebitNoteHeaderPayload,
  DebitNoteLinePayload,
  DebitNotePayload,
} from '@/types/domain'

/* The debit notes raised against one case (Story 5.6, FR-66).
 *
 * `useInvoices.ts`'s twin, deliberately built to the same plan rather than
 * generalised into one parameterised module. The two documents share a shape of
 * client — case-scoped keys, whole-record write responses, an ordering guard —
 * and they do NOT share a lifecycle: an invoice bills a tranche and an issued
 * one displaces it, a debit note displaces nothing; an invoice's create carries
 * a header alone and prefills its lines server-side, a debit note's charges
 * travel WITH the create because a demand with no stated reason is a document
 * nobody pays. A shared generic would make the next divergence a change to both
 * documents at once, which is the same argument `DebitNoteStatus` makes for
 * being its own type beside `InvoiceStatus`.
 *
 * Every key hangs off the case's detail key, for the reason the invoice file
 * gives: the row scope for a note IS the case's, so an entry that outlived the
 * case read it was authorised by would be a list nobody re-checks.
 *
 * NOTHING here is optimistic (UX-DR-24). A debit note is money — and it is the
 * kind of money somebody else is being asked to pay — so the figure that appears
 * is the one the server booked, at the rate the server resolved.
 *
 * Every write answers with the WHOLE note, lines included, so each one seeds the
 * detail cache from the response rather than refetching: adding a charge moves
 * the line's derived amount, the note's total in its own currency AND its booked
 * USD total, and three round trips to learn what one response already carried
 * are three chances to render a stale pair.
 */

/** The two keys, both hung off the case's detail key.
 *
 *  Built from `queryKeys.cases.detail` rather than spelled out, so the prefix
 *  cannot drift from the one the case hooks use — and so invalidating the case
 *  still reaches its debit notes.
 *
 *  `debit-note` singular rather than nesting the detail UNDER the list, exactly
 *  as `invoiceKeys` does and for the same reason: a detail key beginning with
 *  the list key would be invalidated by every list refresh, which would refetch
 *  a note the write response has just delivered in full. Two siblings keep the
 *  seed below meaningful. */
const debitNoteKeys = {
  list: (caseId: string) => [...queryKeys.cases.detail(caseId), 'debit-notes'] as const,
  /** The prefix every debit-note detail on a case shares, so `seed` can reach
   *  the siblings of the one it was handed without spelling the shape twice. */
  details: (caseId: string) => [...queryKeys.cases.detail(caseId), 'debit-note'] as const,
  detail: (caseId: string, noteId: string) =>
    [...debitNoteKeys.details(caseId), noteId] as const,
}

/** Every debit note raised against one case.
 *
 *  The list serves each note WITH its charges, where the invoice list serves a
 *  summary without them — see `DebitNote` in `types/domain`, which argues why.
 *  So this is the read a screen needs to render a note in full, and there is no
 *  second fetch to open one. */
export function useCaseDebitNotes(caseId: string) {
  return useQuery<DebitNote[]>({
    queryKey: debitNoteKeys.list(caseId),
    // No case chosen yet is not an empty case: firing `/cases//debit-notes`
    // would ask the server a question about nothing and render its 404 as a
    // failure.
    enabled: caseId !== '',
    queryFn: async () =>
      (await api.get<{ debitNotes: DebitNote[] }>(`/cases/${caseId}/debit-notes`))
        .debitNotes,
  })
}

/** One debit note — what the editor reads.
 *
 *  Its own key rather than a lookup into the list, because the write responses
 *  below seed exactly this entry: an editor reading the list would hold the row
 *  the last LIST fetch delivered, not the note the last write returned. */
export function useDebitNote(caseId: string, noteId: string) {
  return useQuery<DebitNote>({
    queryKey: debitNoteKeys.detail(caseId, noteId),
    enabled: caseId !== '' && noteId !== '',
    queryFn: () => api.get<DebitNote>(`/cases/${caseId}/debit-notes/${noteId}`),
  })
}

/** Opens a debit note, WITH its charges, in one request (AD-26).
 *
 *  The lines are part of the create rather than added afterwards, and that is
 *  the server's design rather than a convenience taken here: there is no useful
 *  moment at which a debit note with no charges exists, and creating it in one
 *  request is what stops that moment existing at all. The create route refuses
 *  an empty one.
 *
 *  Nothing about the recipient is sent beyond the AD-26 counterparty pair, and
 *  that pair is the case's own client (S-22a: the server refuses any other with
 *  `counterparty: client-only`). The printed name and address are RESOLVED
 *  server-side from it — a body that could state them could address a demand to
 *  a company nobody picked. */
export function useRaiseDebitNote(caseId: string) {
  const queryClient = useQueryClient()
  return useMutation<DebitNote, Error, DebitNotePayload>({
    mutationFn: (payload) =>
      api.post<DebitNote>(`/cases/${caseId}/debit-notes`, payload),
    // Through `seed`, no ticket: the note did not exist before this response, so
    // nothing can have overtaken it.
    onSuccess: (note) => seed(queryClient, caseId, note),
  })
}

/** Edits a draft's header — its date and its recipient reference.
 *
 *  Never who owes it (S-22a): the payload carries no counterparty pair, and the
 *  route keeps the stored payer and printed recipient whatever a body says, so
 *  an amend cannot move a debt. The charges travel on the three line mutations
 *  below, which amend a record that always has at least one.
 *
 *  Every field this moves clears the note's `documentId` server-side, and the
 *  cleared link arrives in the response — it is what the screen's out-of-date
 *  banner reads. */
export function useAmendDebitNote(caseId: string, noteId: string) {
  const queryClient = useQueryClient()
  return useMutation<DebitNote, Error, DebitNoteHeaderPayload, number>({
    mutationFn: (payload) =>
      api.put<DebitNote>(`/cases/${caseId}/debit-notes/${noteId}`, payload),
    onMutate: () => claimWrite(caseId, noteId),
    onSuccess: (note, _payload, ticket) => seed(queryClient, caseId, note, ticket),
  })
}

/** Adds or edits one charge.
 *
 *  ONE mutation for both verbs rather than two, as the invoice editor's is: the
 *  surface has one charge form and a refusal has to land on it, and two
 *  mutations would mean two error states with the editor having to remember
 *  which of them last spoke.
 *
 *  No AMOUNT is sent. It is `unitPrice × quantity`, derived once on the server —
 *  a typed amount that disagrees with its own multiplication is a document that
 *  contradicts itself in front of the person being asked to pay it. */
export function useWriteDebitNoteLine(caseId: string, noteId: string) {
  const queryClient = useQueryClient()
  return useMutation<DebitNote, Error, DebitNoteLinePayload & { lineId?: string }, number>(
    {
      mutationFn: ({ lineId, ...draft }) => {
        /* Normalised for the separators a Russian keyboard produces, and still
           strings. The server's money.D refuses a JSON number outright (AD-9),
           so a client that computed these in floats lands in a bind error rather
           than quietly billing one — but arriving as text is this side's job.

           The qualifying note is sent as '' rather than omitted when empty: the
           printed row always has that line, and `DebitNoteLine.note` is served
           as '' for the same reason. */
        const payload = {
          description: draft.description.trim(),
          note: (draft.note ?? '').trim(),
          unitPrice: normaliseDecimal(draft.unitPrice),
          quantity: normaliseDecimal(draft.quantity),
        }
        const base = `/cases/${caseId}/debit-notes/${noteId}/lines`
        return lineId === undefined
          ? api.post<DebitNote>(base, payload)
          : api.put<DebitNote>(`${base}/${lineId}`, payload)
      },
      onMutate: () => claimWrite(caseId, noteId),
      onSuccess: (note, _payload, ticket) => seed(queryClient, caseId, note, ticket),
    },
  )
}

/** Removes one charge. Answers with the note rather than 204, so the totals a
 *  removal moves — and the render link it clears — arrive with it. */
export function useDropDebitNoteLine(caseId: string, noteId: string) {
  const queryClient = useQueryClient()
  return useMutation<DebitNote, Error, string, number>({
    mutationFn: (lineId) =>
      api.delete<DebitNote>(`/cases/${caseId}/debit-notes/${noteId}/lines/${lineId}`),
    onMutate: () => claimWrite(caseId, noteId),
    onSuccess: (note, _lineId, ticket) => seed(queryClient, caseId, note, ticket),
  })
}

/* ── The ordering guard ──────────────────────────────────────────────────────
 *
 * `useInvoices.ts`'s guard, restated for this document's own keys. Every write
 * answers with the WHOLE note and seeds it into the detail key, which is what
 * makes one round trip enough — and it is also what makes RESPONSE ORDER
 * matter: two charges added in quick succession are two requests in flight at
 * once, and nothing guarantees the second answer arrives second. If the first
 * response lands last it writes a note missing the second charge into the
 * cache, where it stays until something unrelated invalidates it.
 *
 * On this document the stale picture is worse than a missing row. `documentId`
 * travels on the same payload, so a late response can also restore a link the
 * later edit cleared — a screen saying the rendered PDF is current when the
 * server has already decided it is not, which is the state AC 8 exists to make
 * impossible.
 *
 * So each write takes a ticket before it goes out, and only the LATEST ticket
 * for a note is allowed to seed. A late response invalidates the detail instead,
 * which refetches the truth rather than guessing at it.
 *
 * Module state rather than a ref, because the racing mutations are frequently
 * two different component instances — the editor's own form and a row's remove
 * button — and a per-component counter would not see the other. Its own counter
 * and map rather than the invoice file's: the two are separate resources with
 * separate keys, and sharing a sequence would let a busy invoice editor
 * invalidate a debit note's perfectly current seed. */
let writeSequence = 0
const latestWrite = new Map<string, number>()

function writeKeyOf(caseId: string, noteId: string): string {
  return debitNoteKeys.detail(caseId, noteId).join('/')
}

/** Claims the next ticket for one note, and records it as the latest. */
function claimWrite(caseId: string, noteId: string): number {
  const ticket = ++writeSequence
  latestWrite.set(writeKeyOf(caseId, noteId), ticket)
  return ticket
}

/** Writes the served note into its own key and refreshes everything the write
 *  moved beside it.
 *
 *  The list carries the same notes as the detail — in full, charges included —
 *  so refreshing only the one that was written would leave the row behind the
 *  sheet disagreeing with the sheet in front of it.
 *
 *  The SIBLING details are invalidated too. They are less obviously affected
 *  than an invoice's are — there is no case-wide `billedUsd` on this document to
 *  move — but the case's own money read is composed from every note on it, and a
 *  second note open in another tab was fetched under the same case detail key
 *  this write has just aged. Invalidated rather than patched: the server is the
 *  only place this arithmetic is authoritative (UX-DR-24). */
function seed(
  queryClient: ReturnType<typeof useQueryClient>,
  caseId: string,
  note: DebitNote,
  /** The ticket this response's write claimed, when it came from one. A raise
   *  passes none: its note did not exist before the response, so nothing can
   *  have overtaken it. */
  ticket?: number,
) {
  const detail = debitNoteKeys.detail(caseId, note.id)
  const overtaken =
    ticket !== undefined && latestWrite.get(writeKeyOf(caseId, note.id)) !== ticket

  if (overtaken) {
    // A later write is in flight or has already answered, so this payload is the
    // older picture of the two. Refetched rather than seeded.
    void queryClient.invalidateQueries({ queryKey: detail })
  } else {
    queryClient.setQueryData(detail, note)
  }
  void queryClient.invalidateQueries({ queryKey: debitNoteKeys.list(caseId) })
  const siblings = debitNoteKeys.details(caseId)
  void queryClient.invalidateQueries({
    // Every debit-note detail on THIS case except the one just served, which
    // already holds the response the server sent a moment ago.
    predicate: ({ queryKey }) =>
      queryKey.length === siblings.length + 1 &&
      siblings.every((part, i) => queryKey[i] === part) &&
      queryKey[siblings.length] !== note.id,
  })
}
